import { useReducer, Reducer, useState } from 'react';
import { Action } from '../../../types/action.newBackend';
import Backend from '../../../stores/newBackend';

type ActionResult = {
  type: 'results';
  actions: any[];
  total: number;
  currentLink?: string;
  nextParams?: any[];
  previousParams?: any[];
};

type ReducerAction =
  | { type: 'load'; page: number }
  | ActionResult
  | { type: 'error'; error: any };

interface State {
  loading: boolean;
  error: any;
  actions: any[];
  total: number;
  page: number;
  currentLink?: string;
  nextParams?: any[];
  previousParams?: any[];
}

const useGetActionsInitialeState = {
  loading: false,
  error: null,
  actions: [],
  total: 0,
  page: 1,
};

export default function useGetActions(initState: Partial<State>) {
  const [
    {
      loading,
      error,
      actions,
      total,
      nextParams,
      previousParams,
      page,
      currentLink,
    },
    dispatch,
  ] = useReducer<Reducer<State, ReducerAction>>(
    useGetActionsReducer,
    initState
      ? {
          ...useGetActionsInitialeState,
          ...initState,
        }
      : useGetActionsInitialeState
  );

  const [fetchBackend] = useState(() => {
    return async (
      token: string,
      criteria?: any,
      link?: string,
      pageNb?: number
    ) => {
      const pageCursor = pageNb ? pageNb : 1;
      dispatch({
        type: 'load',
        page: pageCursor,
      });

      try {
        const results = await Backend.loadActionsTodo(token, criteria, link);
        const dispatchData: ActionResult = {
          type: 'results',
          actions: results.data.map(parseAction),
          total: results.meta.page.total,
          currentLink: link,
        };

        if (results.links.next) {
          dispatchData.nextParams = [
            token,
            criteria,
            results.links.next as string,
            pageCursor + 1,
          ];
        }

        if (results.links.prev) {
          dispatchData.previousParams = [
            token,
            criteria,
            results.links.prev as string,
            pageCursor - 1,
          ];
        }

        dispatch(dispatchData);
      } catch (e) {
        dispatch({ type: 'error', error: e });
      }
    };
  });

  return {
    loading,
    error,
    actions,
    fetch: fetchBackend,
    total,
    nextParams,
    next:
      nextParams !== undefined
        ? () => fetchBackend(...(nextParams as [string, any, string, number]))
        : undefined,
    previous:
      previousParams !== undefined
        ? () =>
            fetchBackend(...(previousParams as [string, any, string, number]))
        : undefined,
    previousParams,
    page,
    currentLink,
  };
}

const parseAction = (httpAction: any): Action => {
  const action = {
    ...httpAction,
  };

  if (action.lastView) {
    action.lastView = new Date(action.lastView);
  }

  action.history = action.history
    ? action.history.map((h: any) => {
        const newHistory = {
          ...h,
          date: new Date(h.date),
        };

        return newHistory;
      })
    : [];

  return action;
};

function useGetActionsReducer(state: State, action: ReducerAction): State {
  const newState = { ...state };
  switch (action.type) {
    case 'load':
      newState.loading = true;
      newState.actions = [];
      newState.page = action.page;
      newState.error = undefined;
      newState.nextParams = undefined;
      newState.previousParams = undefined;
      break;
    case 'results':
      newState.loading = false;
      newState.actions = action.actions;
      newState.currentLink = action.currentLink;
      newState.total = action.total;
      newState.nextParams = action.nextParams;
      newState.previousParams = action.previousParams;
      break;
    case 'error':
      newState.loading = false;
      newState.error = action.error;
      break;
    default:
      throw new Error();
  }

  return newState;
}
